'use client';

import { memo, useCallback, useEffect, useId, useRef, useState } from "react";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "@tiptap/markdown";
import Underline from "@tiptap/extension-underline";
import type { Editor } from "@tiptap/core";
import {
    Box,
    Divider,
    IconButton,
    Stack,
    styled,
    Tooltip,
    MenuItem,
    Typography,
    Menu,
    Button, Theme
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { neutral } from "@/app/theme/theme-tokens";
import {
    FormatBold,
    FormatItalic,
    FormatListBulleted,
    FormatListNumbered,
    FormatUnderlined,
    Redo,
    Undo
} from "@mui/icons-material";
import { Level } from "@tiptap/extension-heading";
import { BoxProps } from "@mui/system";
import { MarkdownProps, markdownRootSx } from "@/app/features/shared/markdown/Markdown";
import {
    APPLIED_TEXT_SIZE_CLASSES,
    DEFAULT_TEXT_SIZE_CLASS,
    isAppliedTextSizeClass,
    resolveTextSizeClass,
    TEXT_SIZE_LABELS,
    TextSizeClass,
    TextSizeMark
} from "@/app/features/shared/editable-markdown/textSizeMark";

export interface MarkdownEditorProps {
    value: string | undefined;
    onChange?: (value: string) => void;
    commitMode?: 'change' | 'blur';
    label?: string;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    onBlur?: (event?: React.FocusEvent<HTMLElement>) => void;
    autoFocus?: boolean;
    constrainToContainer?: boolean;
    typographyOverrides?: MarkdownProps["typographyOverrides"];
    slots?: {
        root?: BoxProps;
    };
}

function MarkdownEditor({
    value,
    onChange,
    commitMode = 'change',
    disabled,
    label,
    error,
    helperText,
    onBlur,
    autoFocus = false,
    constrainToContainer = false,
    typographyOverrides,
    slots,
}: MarkdownEditorProps) {
    const [hasFocus, setHasFocus] = useState<boolean>(false);
    const [currentTextSize, setCurrentTextSize] = useState<TextSizeClass>(DEFAULT_TEXT_SIZE_CLASS);
    const labelId = useId();

    const highlightTextColor = useCallback((theme: Theme) => {
        if (error) return theme.palette.error.main;
        if (hasFocus) return theme.palette.primary.main;
        return alpha(neutral[50], 1);
    }, [error, hasFocus]);

    const highlightBorderColor = useCallback((theme: Theme) => {
        if (error) return theme.palette.error.main;
        if (hasFocus) return theme.palette.primary.main;
        return alpha(neutral[50], .25);
    }, [error, hasFocus]);

    const highlightBorder = useCallback((theme: Theme) => {

        const color = highlightBorderColor(theme);

        if (error) {
            if (hasFocus) return `2px solid ${color}`;
            else return `1px solid ${color}`;
        }
        if (hasFocus) return `2px solid ${color}`;
        return `1px solid ${color}`;
    }, [error, hasFocus, highlightBorderColor])

    const isApplyingFromProps = useRef<boolean>(false);
    const onChangeRef = useRef(onChange);
    const commitModeRef = useRef(commitMode);
    const valueRef = useRef(value ?? '');
    const latestEditorValueRef = useRef(value ?? '');
    const lastEmittedValueRef = useRef(value ?? '');

    useEffect(() => {
        onChangeRef.current = onChange;
    }, [onChange]);

    useEffect(() => {
        commitModeRef.current = commitMode;
    }, [commitMode]);

    useEffect(() => {
        valueRef.current = value ?? '';
    }, [value]);

    const updateCurrentTextSizeFromEditor = useCallback((editorInstance: Editor) => {
        const activeTextSize = editorInstance.getAttributes('textSize')?.sizeClass;
        const nextSize = resolveTextSizeClass(activeTextSize);
        setCurrentTextSize((previousSize) => previousSize === nextSize ? previousSize : nextSize);
    }, []);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Markdown,
            Underline,
            TextSizeMark,
        ],
        content: value,
        contentType: 'markdown',
        onUpdate: ({ editor }) => {
            updateCurrentTextSizeFromEditor(editor);
            if (isApplyingFromProps.current) return; // Guard gegen Feedback-Loop
            const nextValue = editor.getMarkdown();
            latestEditorValueRef.current = nextValue;
            if (commitModeRef.current === 'change') {
                lastEmittedValueRef.current = nextValue;
                onChangeRef.current?.(nextValue);
            }
        },
        onSelectionUpdate: ({ editor }) => updateCurrentTextSizeFromEditor(editor),
        onCreate: ({ editor }) => updateCurrentTextSizeFromEditor(editor),
        immediatelyRender: false,
    }, [updateCurrentTextSizeFromEditor]);

    useEffect(() => {
        if (!editor) return;
        editor.setEditable(!disabled);
    }, [editor, disabled]);

    useEffect(() => {
        if (!editor || !autoFocus || disabled) return;
        queueMicrotask(() => {
            if (!editor.isDestroyed) editor.commands.focus('start');
        });
    }, [editor, autoFocus, disabled]);

    useEffect(() => {
        if (!editor) return;

        if ((value ?? '') === (lastEmittedValueRef.current ?? '')) {
            return;
        }

        const current = editor.getMarkdown();
        if ((value ?? '') !== (current ?? '')) {
            isApplyingFromProps.current = true;
            editor.commands.setContent(value ?? '', { emitUpdate: false });
            isApplyingFromProps.current = false;
            latestEditorValueRef.current = value ?? '';
            lastEmittedValueRef.current = value ?? '';
            queueMicrotask(() => updateCurrentTextSizeFromEditor(editor));
        }
    }, [editor, value, updateCurrentTextSizeFromEditor]);

    const applyTextSizeToSelection = useCallback((sizeClass: TextSizeClass) => {
        if (!editor) return;
        const chain = editor.chain().focus();

        if (sizeClass === DEFAULT_TEXT_SIZE_CLASS) {
            chain.unsetTextSize().run();
            return;
        }

        if (!isAppliedTextSizeClass(sizeClass)) return;
        chain.setTextSize(sizeClass).run();
    }, [editor]);

    function onEditorFocus() {
        setHasFocus(true);
    }

    function onEditorBlur(event: React.FocusEvent<HTMLElement>) {
        setHasFocus(false);
        const latestValue = latestEditorValueRef.current;
        if (commitModeRef.current === 'blur' && latestValue !== valueRef.current) {
            lastEmittedValueRef.current = latestValue;
            onChangeRef.current?.(latestValue);
        }
        onBlur?.(event);
    }

    if (!editor) return null;


    return (
        <Box
            {...slots?.root}
            className={['markdown-root', slots?.root?.className].filter(Boolean).join(' ')}
            sx={[
                (theme) => markdownRootSx(theme, typographyOverrides),
                ...(slots?.root?.sx
                    ? (Array.isArray(slots.root.sx) ? slots.root.sx : [slots.root.sx])
                    : []),
            ]}
        >
            <Box
                className="markdown-editor flex flex-col flex-1 max-h-full"
                sx={(theme) => ({
                    border: highlightBorder(theme),
                    boxSizing: 'border-box',
                    minWidth: 0,
                    position: 'relative',
                    color: (theme) => theme.palette.text.primary,
                    borderRadius: '8px',
                    overflow: 'visible',
                    transition: 'border 0.3s ease',
                    '*:focus-visible': {
                        outline: 'none !important',
                    },
                    '& .ProseMirror:focus': {
                        outline: 'none',
                        backgroundColor: 'transparent !important',
                    },
                    '& .ProseMirror:focus-visible': {
                        outline: 'none !important',
                        backgroundColor: 'transparent !important',
                    },
                })}
            >
                {label && (
                    <FloatingLabel
                        // @ts-expect-error - dont know why
                        component="label"
                        id={labelId}
                        shrink={hasFocus || !!value}
                        sx={{
                            color: (theme) => highlightTextColor(theme),
                            backgroundColor: (theme) => theme.palette.background.paper,
                        }}
                    >
                        {label}
                    </FloatingLabel>
                )}
                <Box
                    className="markdown-editor-content"
                    sx={{
                        flex: '1 1 auto',
                        p: 2,
                        minHeight: 0,
                        overflowY: 'auto',
                        '& .ProseMirror p.is-editor-empty:first-of-type::before': {
                            content: 'attr(data-placeholder)',
                            float: 'left',
                            color: (theme) => theme.palette.text.disabled,
                            pointerEvents: 'none',
                            height: 0,
                            whiteSpace: 'pre-wrap'
                        },
                        '& .ProseMirror': {
                            minHeight: constrainToContainer ? 0 : '100px',
                            overflowX: 'hidden',
                        },
                        '& .ProseMirror .md-text-small': { fontSize: '0.875em' },
                        '& .ProseMirror .md-text-large': { fontSize: '1.5em' },
                        '& .ProseMirror .md-text-xlarge': { fontSize: '2em' },
                    }}
                >

                    <EditorContent
                        disabled={disabled}
                        editor={editor}
                        aria-labelledby={label ? labelId : undefined}
                        aria-label={label ? undefined : 'Markdown bearbeiten'}
                        onFocus={onEditorFocus}
                        onBlur={onEditorBlur}
                    />
                </Box>

                <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                        width: '100%',
                        borderRadius: '8px',
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        minWidth: 0,
                        px: 2,
                        minHeight: '48px',
                        flexShrink: 0,
                        borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                        backgroundColor: 'transparent',
                    }}
                >
                    <Stack
                        direction="row"
                        sx={{
                            flexWrap: 'nowrap',
                            flex: '0 0 auto',
                            minWidth: 'max-content',
                        }}
                    >

                        <Tooltip title="Rückgängig">
                            <SquareIconButton aria-label="Rückgängig" onClick={() => editor?.chain().focus().undo().run()}>
                                <Undo />
                            </SquareIconButton>
                        </Tooltip>

                        <Tooltip title="Wiederholen">
                            <SquareIconButton aria-label="Wiederholen" onClick={() => editor?.chain().focus().redo().run()}>
                                <Redo />
                            </SquareIconButton>
                        </Tooltip>

                        <Divider orientation="vertical"  color="primary" sx={{ mx: 1 }} />

                        <Tooltip title="Fett">
                            <SquareIconButton aria-label="Fett" onClick={() => editor?.chain().focus().toggleBold().run()}>
                                <FormatBold />
                            </SquareIconButton>
                        </Tooltip>

                        <Tooltip title="Kursiv">
                            <SquareIconButton aria-label="Kursiv" onClick={() => editor?.chain().focus().toggleItalic().run()}>
                                <FormatItalic />
                            </SquareIconButton>
                        </Tooltip>


                        <Tooltip title="Unterstrichen">
                            <SquareIconButton aria-label="Unterstrichen" onClick={() => editor?.chain().focus().toggleUnderline().run()}>
                                <FormatUnderlined />
                            </SquareIconButton>
                        </Tooltip>

                        <Divider orientation="vertical"  color="primary" sx={{ mx: 1 }}/>

                        <Tooltip title="Stichpunkte">
                            <SquareIconButton aria-label="Stichpunkte" onClick={() => editor?.chain().focus().toggleBulletList().run()}>
                                <FormatListBulleted />
                            </SquareIconButton>
                        </Tooltip>

                        <Tooltip title="Aufzählung">
                            <SquareIconButton aria-label="Nummerierte Liste" onClick={() => editor?.chain().focus().toggleOrderedList().run()}>
                                <FormatListNumbered />
                            </SquareIconButton>
                        </Tooltip>
                        <Divider orientation="vertical"  color="primary" sx={{ mx: 1 }} />

                        <Tooltip title="Überschrift">
                            <HeadingMenu
                                setHeading={(heading) =>
                                    heading
                                        ? editor?.chain().focus().setHeading({ level: heading }).run()
                                        : editor?.chain().focus().setParagraph().run()
                                }
                            />
                        </Tooltip>

                        <Tooltip title="Textgröße (markierten Text ändern)">
                            <TextSizeMenu
                                currentTextSize={currentTextSize}
                                setTextSize={applyTextSizeToSelection}
                            />
                        </Tooltip>
                    </Stack>
                </Stack>
            </Box>
            {helperText && (
                <Typography
                    color="error"
                    sx={{
                        marginTop: '3px !important',
                        marginLeft: '14px !important',
                        fontSize: '1rem !important',
                        fontStyle: 'italic',
                        fontWeight: 'bold',
                        lineHeight: '1.4 !important',
                        letterSpacing: '0.05em',
                    }}
                >
                    {helperText}
                </Typography>
            )}
        </Box>
    );
}

export default memo(MarkdownEditor);


function HeadingMenu({ setHeading }: { setHeading: (heading: Level | null) => void }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const closeMenu = () => {
        setAnchorEl(null);
    }

    const selectHeading = (heading: Level | null) => {
        setHeading(heading);
        closeMenu();
    };

    const headings: string[] = Array.from({ length: 6 }).map((_, index) => `h${index+1}`)

    return (
        <div>
            <Button
                id="headings-button"
                variant="text"
                color="inherit"
                aria-controls={open ? 'headings-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={openMenu}
                sx={{ color: 'text.secondary' }}
            >
                Überschrift
            </Button>
            <Menu
                id="headings-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={closeMenu}
                slotProps={{
                    root: {
                        'aria-labelledby': 'headings-button',
                    },
                }}
            >
                <MenuItem onClick={() => selectHeading(null)}>
                    <Typography variant="body1" color="text.secondary">Keine</Typography>
                </MenuItem>
                {headings.map((heading, index) => (
                    <MenuItem key={index} onClick={() => selectHeading(index+1 as Level)}>
                        {/* @ts-expect-error - ignore */}
                        <Typography variant={heading} color="text.secondary">{heading.toUpperCase()}</Typography>
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
}

function TextSizeMenu({
    currentTextSize,
    setTextSize
}: {
    currentTextSize: TextSizeClass;
    setTextSize: (sizeClass: TextSizeClass) => void
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const openMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const closeMenu = () => {
        setAnchorEl(null);
    };

    const textSizes: { label: string; sizeClass: TextSizeClass }[] = [
        { label: TEXT_SIZE_LABELS['md-text-normal'], sizeClass: 'md-text-normal' },
        ...APPLIED_TEXT_SIZE_CLASSES.map((sizeClass) => ({
            label: TEXT_SIZE_LABELS[sizeClass],
            sizeClass,
        })),
    ];

    const selectTextSize = (sizeClass: TextSizeClass) => {
        setTextSize(sizeClass);
        closeMenu();
    };

    return (
        <div>
            <Button
                id="text-size-button"
                variant="text"
                color="inherit"
                aria-controls={open ? 'text-size-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={openMenu}
                sx={(theme) => ({
                    color: currentTextSize === DEFAULT_TEXT_SIZE_CLASS ? theme.palette.text.secondary : theme.palette.primary.main,
                    fontWeight: currentTextSize === DEFAULT_TEXT_SIZE_CLASS ? 400 : 600,
                    textWrap: 'nowrap',
                })}
            >
                Textgröße: {TEXT_SIZE_LABELS[currentTextSize]}
            </Button>
            <Menu
                id="text-size-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={closeMenu}
                slotProps={{
                    root: {
                        'aria-labelledby': 'text-size-button',
                    },
                }}
            >
                {textSizes.map((textSize) => (
                    <MenuItem
                        key={textSize.sizeClass}
                        selected={textSize.sizeClass === currentTextSize}
                        onClick={() => selectTextSize(textSize.sizeClass)}
                    >
                        <Typography
                            color="text.secondary"
                            sx={{
                                ...(textSize.sizeClass === 'md-text-small' ? { fontSize: '0.875rem' } : {}),
                                ...(textSize.sizeClass === 'md-text-normal' ? { fontSize: '1rem', fontWeight: 600 } : {}),
                                ...(textSize.sizeClass === 'md-text-large' ? { fontSize: '1.15rem' } : {}),
                                ...(textSize.sizeClass === 'md-text-xlarge' ? { fontSize: '1.3rem' } : {}),
                            }}
                        >
                            {textSize.label}
                        </Typography>
                    </MenuItem>
                ))}
            </Menu>
        </div>
    );
}

const SquareIconButton = styled(IconButton) ({
    borderRadius: 'unset'
});

const FloatingLabel = styled(Box, {
    shouldForwardProp: (prop) => !['shrink'].includes(prop as string)
})<{ shrink: boolean }> (({ theme, shrink }) => ({
    position: 'absolute',
    left: '10px',
    top: '18px',
    transformOrigin: 'left top',
    transition: 'top 150ms ease, transform 150ms ease, color 150ms ease, background-color 150ms ease',
    padding: '0 8px',
    backgroundColor: theme.palette.background.paper,
    pointerEvents: 'none',
    fontSize: '18px',
    lineHeight: '1.4375em',
    borderRadius: '2px',
    ...(shrink && {
        top: '-10px',
        zIndex: 100,
        transform: 'scale(0.75)',
    })
}));
